import React, { useState } from 'react';
import { Button } from '@cokitchen/cokitchen-components';
import LocalModal from 'components/base/local-modal';
import ReactCrop, { makeAspectCrop, type Crop, centerCrop } from 'react-image-crop';
import 'react-image-crop/src/ReactCrop.scss';

const ASPECT_RATIO = 1;
const MIN_DIMENSION = 150;

const CropImageModal = ({
  show,
  onClose,
  src,
  imgType,
  saveImage,
  setCroppedImage,
  circularCrop,
  aspectRatio = ASPECT_RATIO
}: {
  show: boolean;
  onClose: () => void;
  src: string;
  setCroppedImage: (blobUrl: string) => void;
  imgType: string;
  saveImage: () => void;
  circularCrop: boolean;
  aspectRatio?: number;
}) => {
  const [crop, setCrop] = useState<Crop>();
  const [image, setImage] = useState<HTMLImageElement | null>(null);

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const loadedImage = e.currentTarget;
    setImage(loadedImage);

    const { width, height } = e.currentTarget;
    const cropWidthInPercent = (MIN_DIMENSION / width) * 100;

    const crop = makeAspectCrop(
      {
        unit: '%',
        width: cropWidthInPercent
      },
      aspectRatio,
      width,
      height
    );
    const centeredCrop = centerCrop(crop, width, height);
    setCrop(centeredCrop);
  };

  const makeClientCrop = async (crop: Crop) => {
    if (image && crop.width && crop.height) {
      const croppedImg = await getCroppedImg(image, crop);
      setCroppedImage(croppedImg);
    }
  };

  const getCroppedImg = (sourceImage: any, crop: Crop): Promise<string> => {
    const canvas = document.createElement('canvas');
    const scaleX = sourceImage.naturalWidth / sourceImage.width;
    const scaleY = sourceImage.naturalHeight / sourceImage.height;
    canvas.width = crop.width;
    canvas.height = crop.height;
    const ctx = canvas.getContext('2d');

    if (ctx) {
      ctx.drawImage(
        sourceImage,
        crop.x * scaleX,
        crop.y * scaleY,
        crop.width * scaleX,
        crop.height * scaleY,
        0,
        0,
        crop.width,
        crop.height
      );
    }

    return new Promise((resolve) => {
      canvas.toBlob((file) => {
        if (file) resolve(URL.createObjectURL(file));
      }, `image/${imgType.split('/')[1]}`);
    });
  };

  return (
    <LocalModal
      isOpen={show}
      title='Crop Photo'
      shouldCloseOnOverlayClick={false}
      onRequestClose={() => onClose()}
      footer={
        <div className='flex gap-5 justify-end '>
          <Button className='w-[130px]' color='primary' small variant='block' onClick={saveImage}>
            Save
          </Button>
        </div>
      }>
      <div className='flex items-center justify-center'>
        <ReactCrop
          ruleOfThirds
          crop={crop}
          keepSelection
          circularCrop={circularCrop}
          aspect={aspectRatio}
          minWidth={MIN_DIMENSION}
          onChange={(pixelCrop: any, percentCrop: any) => setCrop(percentCrop)}
          onComplete={(crop: any) => {
            if (image) makeClientCrop(crop);
          }}>
          <img src={src} alt='' className='mx-auto' onLoad={onImageLoad} />
        </ReactCrop>
      </div>
    </LocalModal>
  );
};

export default CropImageModal;
